/**
 * LaunchpadCreatePage
 *
 * Multi-step wizard for creating a new launchpad.
 *
 * Steps:
 * 1. Collection Info - Name, slug, description, taxon, socials
 * 2. Images - Thumbnail (required for validation) and banner (optional)
 * 3. Pricing & Phases - Royalty %, phase configuration
 * 4. Assets - Upload NFT files (folder with images/metadata)
 * 5. Review - Show validation results, link to dashboard
 *
 * Accessible via: /launchpad/new
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { DefaultPageLayout } from '../ui/layouts/DefaultPageLayout';
import { Button } from '../ui/components/Button';
import { useAuthStore } from '../store/authStore';
import {
  createLaunch,
  getLaunch,
  checkSlugAvailable,
  generateSlug,
  updateLaunch,
  type CreateLaunchInput,
  type UpdateLaunchInput,
  type LaunchWithStats,
} from '../api/launches';
import {
  getMyProjects,
  createProject,
  type ManagedProject,
  type CreateProjectInput,
} from '../api/creator';
import { FeatherArrowLeft, FeatherArrowRight } from '@subframe/core';
import { Select } from '../ui/components/Select';
import { TextField } from '../ui/components/TextField';
import { TextArea } from '../ui/components/TextArea';
import {
  HorizontalWizardStepper,
  WizardStepCollectionInfo,
  WizardStepImages,
  WizardStepPricing,
  WizardStepAssets,
  WizardStepEditions,
  WizardStepReview,
  type StepStatus,
  type WizardStep,
  type CollectionInfoFormData,
  type CollectionType,
  type PricingFormData,
} from '../components/launchpad/wizard';
import { WizardStepComposableTraits } from '../components/launchpad/wizard/WizardStepComposableTraits';
import { featureFlags } from '../config/featureFlags';

type WizardStepNumber = 0 | 1 | 2 | 3 | 4 | 5;

const STANDARD_STEPS: WizardStep[] = [
  { number: 1, title: 'Collection Info' },
  { number: 2, title: 'Images' },
  { number: 3, title: 'Pricing & Phases' },
  { number: 4, title: 'Assets' },
  { number: 5, title: 'Review' },
];

const COMPOSABLE_STEPS: WizardStep[] = [
  { number: 1, title: 'Collection Info' },
  { number: 2, title: 'Images' },
  { number: 3, title: 'Pricing & Phases' },
  { number: 4, title: 'Traits' },
  { number: 5, title: 'Review' },
];

const EDITION_STEPS: WizardStep[] = [
  { number: 1, title: 'Collection Info' },
  { number: 2, title: 'Images' },
  { number: 3, title: 'Pricing & Phases' },
  { number: 4, title: 'Editions' },
  { number: 5, title: 'Review' },
];

const STANDARD_DESCRIPTIONS: Record<Exclude<WizardStepNumber, 0>, string> = {
  1: 'Basic information about your collection',
  2: 'Upload thumbnail and banner images',
  3: 'Configure mint pricing and phases',
  4: 'Upload your NFT files',
  5: 'Review and continue to dashboard',
};

const COMPOSABLE_DESCRIPTIONS: Record<Exclude<WizardStepNumber, 0>, string> = {
  1: 'Basic information about your Build Your Own collection',
  2: 'Upload thumbnail and banner images',
  3: 'Configure mint pricing, phases, and build fee',
  4: 'Define trait categories and upload trait images',
  5: 'Review and continue to dashboard',
};

const EDITION_DESCRIPTIONS: Record<Exclude<WizardStepNumber, 0>, string> = {
  1: 'Basic information about your edition collection',
  2: 'Upload thumbnail and banner images',
  3: 'Configure the default mint price and phases',
  4: 'Add your artworks and set copies per edition',
  5: 'Review and continue to dashboard',
};

export const LaunchpadCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useAuthStore();

  // Wizard state
  const [currentStep, setCurrentStep] = useState<WizardStepNumber>(0);
  const [stepStatus, setStepStatus] = useState<Record<Exclude<WizardStepNumber, 0>, StepStatus>>({
    1: 'pending',
    2: 'pending',
    3: 'pending',
    4: 'pending',
    5: 'pending',
  });
  const [loading, setLoading] = useState(false);
  const [traitUploading, setTraitUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Project association state (pre-step before type selection)
  type PreWizardScreen = 'project' | 'type';
  type ProjectChoice = 'none' | 'existing' | 'new';
  const [preWizardScreen, setPreWizardScreen] = useState<PreWizardScreen>('project');
  const [projectChoice, setProjectChoice] = useState<ProjectChoice>('none');
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [myProjects, setMyProjects] = useState<ManagedProject[]>([]);
  const [projectsLoaded, setProjectsLoaded] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectDescription, setNewProjectDescription] = useState('');
  const [creatingProject, setCreatingProject] = useState(false);

  // Load user's projects for the "existing project" dropdown
  useEffect(() => {
    if (isAuthenticated && !projectsLoaded) {
      getMyProjects()
        .then((projects) => {
          setMyProjects(projects);
          setProjectsLoaded(true);
        })
        .catch((err) => {
          console.error('Failed to load projects:', err);
          setProjectsLoaded(true);
        });
    }
  }, [isAuthenticated, projectsLoaded]);

  // Launch data (created after step 1)
  const [launchId, setLaunchId] = useState<string | null>(null);
  const [launch, setLaunch] = useState<LaunchWithStats | null>(null);

  // Step 1: Collection Info
  const [collectionInfo, setCollectionInfo] = useState<CollectionInfoFormData>({
    name: '',
    slug: '',
    description: '',
    taxon: '',
    websiteUrl: '',
    twitterHandle: '',
    discordUrl: '',
    collectionType: 'standard',
  });

  // Dynamic wizard config based on collection type
  const isComposable = collectionInfo.collectionType === 'composable';
  const isEdition = collectionInfo.collectionType === 'edition';
  const WIZARD_STEPS = isComposable ? COMPOSABLE_STEPS : isEdition ? EDITION_STEPS : STANDARD_STEPS;
  const STEP_DESCRIPTIONS = isComposable
    ? COMPOSABLE_DESCRIPTIONS
    : isEdition
      ? EDITION_DESCRIPTIONS
      : STANDARD_DESCRIPTIONS;
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [slugAvailable, setSlugAvailable] = useState<boolean | null>(null);
  const [slugChecking, setSlugChecking] = useState(false);
  const [taxonError, setTaxonError] = useState<string | null>(null);

  // Step 3: Pricing
  const [pricingData, setPricingData] = useState<PricingFormData>({
    transferFee: 0,
    nftFlags: 8, // Default: tfTransferable (8) only
    allowlistEnabled: false,
    allowlistMintPriceDrops: 0,
    allowlist2Enabled: false,
    allowlist2MintPriceDrops: 0,
    mintPriceDrops: 0,
    limitPerWallet: null,
  });

  // Resume wizard from existing launch (dev/testing helper)
  // Usage: /launchpad/new?resume=<launchId>&step=3
  useEffect(() => {
    const resumeIdRaw = searchParams.get('resume');
    const stepParam = searchParams.get('step');
    const resumeId = resumeIdRaw?.trim();
    if (!resumeId || launchId) return; // Already loaded or no resume param

    const loadExistingLaunch = async () => {
      try {
        setLoading(true);

        // Validate UUID format before making API call
        const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        console.log('Resuming launch with ID:', JSON.stringify(resumeId), 'length:', resumeId.length, 'valid:', UUID_REGEX.test(resumeId));

        if (!UUID_REGEX.test(resumeId)) {
          throw new Error(`Invalid launch ID format: "${resumeId}"`);
        }

        const existingLaunch = await getLaunch(resumeId);
        setLaunchId(existingLaunch.id);
        setLaunch(existingLaunch);

        // Set collection type from existing launch so the right step-4 UI
        // (Assets / Traits / Editions) is shown on resume.
        const resumedType = existingLaunch.collection_type;
        if (resumedType === 'composable' || resumedType === 'edition') {
          setCollectionInfo((prev) => ({ ...prev, collectionType: resumedType }));
          setSelectedType(resumedType);
        }

        // Initialize pricing data (ensure numbers, API might return strings)
        setPricingData({
          transferFee: Number(existingLaunch.transfer_fee) || 0,
          nftFlags: existingLaunch.nft_flags ?? 8,
          allowlistEnabled: existingLaunch.allowlist_enabled,
          allowlistMintPriceDrops: Number(existingLaunch.allowlist_mint_price_drops) || 0,
          allowlist2Enabled: existingLaunch.allowlist_2_enabled,
          allowlist2MintPriceDrops: Number(existingLaunch.allowlist_2_mint_price_drops) || 0,
          mintPriceDrops: Number(existingLaunch.mint_price_drops) || 0,
          limitPerWallet: existingLaunch.limit_per_wallet ? Number(existingLaunch.limit_per_wallet) : null,
        });

        // Mark step 1 as completed
        setStepStatus((prev) => ({ ...prev, 1: 'completed' }));

        // Go to requested step (default step 2)
        const targetStep = stepParam ? parseInt(stepParam, 10) : 2;
        if (targetStep >= 2 && targetStep <= 5) {
          setCurrentStep(targetStep as WizardStepNumber);
        } else {
          setCurrentStep(2);
        }
      } catch (err) {
        console.error('Failed to resume launch:', err);
        setError('Failed to load launch. It may not exist or you may not have access.');
      } finally {
        setLoading(false);
      }
    };

    loadExistingLaunch();
  }, [searchParams, launchId]);

  // Auto-generate slug from name
  useEffect(() => {
    if (!slugManuallyEdited && collectionInfo.name) {
      setCollectionInfo((prev) => ({
        ...prev,
        slug: generateSlug(collectionInfo.name),
      }));
    }
  }, [collectionInfo.name, slugManuallyEdited]);

  // Check slug availability with debounce
  useEffect(() => {
    if (!collectionInfo.slug || collectionInfo.slug.length < 3) {
      setSlugAvailable(null);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        setSlugChecking(true);
        // Exclude the current launch from the uniqueness check when editing a draft
        const available = await checkSlugAvailable(collectionInfo.slug, launchId ?? undefined);
        setSlugAvailable(available);
      } catch (err) {
        console.error('Failed to check slug:', err);
        setSlugAvailable(null);
      } finally {
        setSlugChecking(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [collectionInfo.slug, launchId]);

  // Validate taxon
  const validateTaxon = useCallback((value: string): string | null => {
    if (!value.trim()) {
      return 'Taxon is required';
    }
    const num = parseInt(value, 10);
    if (isNaN(num)) {
      return 'Taxon must be a number';
    }
    if (num < 0) {
      return 'Taxon cannot be negative';
    }
    if (num > 2147483647) {
      return 'Taxon must be at most 2,147,483,647';
    }
    return null;
  }, []);

  // Update taxon error when value changes
  useEffect(() => {
    if (collectionInfo.taxon) {
      setTaxonError(validateTaxon(collectionInfo.taxon));
    }
  }, [collectionInfo.taxon, validateTaxon]);

  // Validate step 1
  const isStep1Valid = useCallback(() => {
    const taxonValidation = validateTaxon(collectionInfo.taxon);
    return (
      collectionInfo.name.trim().length >= 3 &&
      collectionInfo.slug.length >= 3 &&
      /^[a-z0-9-]+$/.test(collectionInfo.slug) &&
      slugAvailable === true &&
      collectionInfo.description.trim().length > 0 &&
      taxonValidation === null
    );
  }, [collectionInfo, slugAvailable, validateTaxon]);

  // Handle step 1 completion - create the launch draft (or just advance if draft already exists)
  const handleStep1Complete = async () => {
    if (!isStep1Valid()) return;

    // If draft already exists (e.g. user navigated back from step 2), just advance
    if (launchId) {
      setStepStatus((prev) => ({ ...prev, 1: 'completed' }));
      setCurrentStep(2);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const input: CreateLaunchInput = {
        name: collectionInfo.name.trim(),
        slug: collectionInfo.slug.trim(),
        description: collectionInfo.description.trim() || null,
        taxon: parseInt(collectionInfo.taxon, 10),
        website_url: collectionInfo.websiteUrl.trim() || null,
        twitter_handle: collectionInfo.twitterHandle.trim() || null,
        discord_url: collectionInfo.discordUrl.trim() || null,
        ...(isComposable ? { collection_type: 'composable' as const } : {}),
        ...(isEdition ? { collection_type: 'edition' as const } : {}),
        ...(selectedProjectId ? { project_id: selectedProjectId } : {}),
      };

      const createdLaunch = await createLaunch(input);
      setLaunchId(createdLaunch.id);

      // Fetch full launch data
      const fullLaunch = await getLaunch(createdLaunch.id);
      setLaunch(fullLaunch);

      // Initialize pricing data from launch (ensure numbers)
      setPricingData({
        transferFee: Number(fullLaunch.transfer_fee) || 0,
        nftFlags: fullLaunch.nft_flags ?? 8,
        allowlistEnabled: fullLaunch.allowlist_enabled,
        allowlistMintPriceDrops: Number(fullLaunch.allowlist_mint_price_drops) || 0,
        allowlist2Enabled: fullLaunch.allowlist_2_enabled,
        allowlist2MintPriceDrops: Number(fullLaunch.allowlist_2_mint_price_drops) || 0,
        mintPriceDrops: Number(fullLaunch.mint_price_drops) || 0,
        limitPerWallet: fullLaunch.limit_per_wallet ? Number(fullLaunch.limit_per_wallet) : null,
      });

      // Mark step 1 as completed and move to step 2
      setStepStatus((prev) => ({ ...prev, 1: 'completed' }));
      setCurrentStep(2);
    } catch (err) {
      console.error('Failed to create launch:', err);
      setError(err instanceof Error ? err.message : 'Failed to create launch');
    } finally {
      setLoading(false);
    }
  };

  // Refresh launch data
  const refreshLaunch = async () => {
    if (!launchId) return;
    try {
      const updatedLaunch = await getLaunch(launchId);
      setLaunch(updatedLaunch);
    } catch (err) {
      console.error('Failed to refresh launch:', err);
    }
  };

  // Step 0: Type selection (local state before committing to collectionInfo)
  const [selectedType, setSelectedType] = useState<CollectionType>(collectionInfo.collectionType);

  // Handle navigation
  const goToStep = (step: number) => {
    // Can only go back to completed/skipped steps or forward if allowed
    if (step < currentStep && step >= 1 && step <= 5) {
      setCurrentStep(step as WizardStepNumber);
    }
  };

  const handleBack = () => {
    if (currentStep === 0 && preWizardScreen === 'type') {
      setPreWizardScreen('project');
    } else if (currentStep === 0) {
      navigate('/launchpad');
    } else if (currentStep === 1 && !launchId) {
      setCurrentStep(0);
    } else if (currentStep === 1 && launchId) {
      navigate('/launchpad');
    } else {
      setCurrentStep((prev) => (prev - 1) as WizardStepNumber);
    }
  };

  const handleSkip = () => {
    // Mark current step as skipped and move to next
    setStepStatus((prev) => ({ ...prev, [currentStep]: 'skipped' }));
    setCurrentStep((prev) => (prev + 1) as WizardStepNumber);
  };

  // Save pricing data to API
  const savePricing = async () => {
    if (!launchId) return;
    // Ensure all numeric fields are numbers (not strings)
    const updateData: UpdateLaunchInput = {
      transfer_fee: Number(pricingData.transferFee) || 0,
      nft_flags: pricingData.nftFlags,
      allowlist_enabled: pricingData.allowlistEnabled,
      allowlist_mint_price_drops: Number(pricingData.allowlistMintPriceDrops) || 0,
      allowlist_2_enabled: pricingData.allowlist2Enabled,
      allowlist_2_mint_price_drops: Number(pricingData.allowlist2MintPriceDrops) || 0,
      mint_price_drops: Number(pricingData.mintPriceDrops) || 0,
      limit_per_wallet: pricingData.limitPerWallet ? Number(pricingData.limitPerWallet) : null,
    };
    await updateLaunch(launchId, updateData);
  };

  const handleContinue = async () => {
    try {
      setLoading(true);
      setError(null);

      // Save pricing on step 3
      if (currentStep === 3) {
        await savePricing();
      }

      // Mark current step as completed and move to next
      setStepStatus((prev) => ({ ...prev, [currentStep]: 'completed' }));
      setCurrentStep((prev) => (prev + 1) as WizardStepNumber);
    } catch (err) {
      console.error('Failed to save:', err);
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAndExit = async () => {
    try {
      setLoading(true);
      setError(null);

      // Save pricing on step 3
      if (currentStep === 3) {
        await savePricing();
      }

      // Navigate to dashboard
      if (launchId) {
        navigate(`/launchpad/${launchId}`);
      } else {
        navigate('/launchpad');
      }
    } catch (err) {
      console.error('Failed to save:', err);
      setError(err instanceof Error ? err.message : 'Failed to save');
      setLoading(false);
    }
  };

  const handleGoToDashboard = () => {
    if (launchId) {
      navigate(`/launchpad/${launchId}`);
    }
  };

  // Can skip steps 2-4
  const canSkip = currentStep >= 2 && currentStep <= 4;

  // Show login prompt if not authenticated
  if (!isAuthenticated) {
    return (
      <DefaultPageLayout>
        <div className="container max-w-4xl mx-auto px-4 py-12">
          <div className="flex flex-col items-center justify-center text-center gap-4">
            <h1 className="text-heading-2 font-heading-2 text-default-font">
              Launch New Collection
            </h1>
            <p className="text-body font-body text-subtext-color max-w-md">
              Connect your wallet to create your NFT collection.
            </p>
          </div>
        </div>
      </DefaultPageLayout>
    );
  }

  // Show loading while resuming a launch (wait until launch is loaded)
  const resumeId = searchParams.get('resume');
  if (resumeId && !launchId) {
    return (
      <DefaultPageLayout>
        <div className="container max-w-4xl mx-auto px-4 py-12">
          <div className="flex flex-col items-center justify-center text-center gap-4">
            {error ? (
              <>
                <p className="text-body font-body text-error-600">{error}</p>
                <Button onClick={() => navigate('/launchpad')}>Back to Collections</Button>
              </>
            ) : (
              <p className="text-body font-body text-subtext-color">Loading launch...</p>
            )}
          </div>
        </div>
      </DefaultPageLayout>
    );
  }

  // Project pre-step continue handler
  const handleProjectContinue = async () => {
    if (projectChoice === 'new') {
      if (!newProjectName.trim()) return;
      try {
        setCreatingProject(true);
        setError(null);
        const newProject = await createProject({ name: newProjectName.trim(), description: newProjectDescription.trim() || null });
        setSelectedProjectId(newProject.id);
        setMyProjects((prev) => [...prev, { id: newProject.id, slug: newProject.slug, name: newProject.name, image_url: null, description: null, collection_count: 0, role: 'owner' }]);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to create project');
        return;
      } finally {
        setCreatingProject(false);
      }
    }
    setPreWizardScreen('type');
  };

  // Step 0 continue handler
  const handleStep0Continue = () => {
    setCollectionInfo((prev) => ({ ...prev, collectionType: selectedType }));
    setCurrentStep(1);
  };

  return (
    <DefaultPageLayout>
      <div className="container max-w-2xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/launchpad')}
            className="flex items-center gap-2 text-caption font-caption text-subtext-color hover:text-default-font mb-4"
          >
            <FeatherArrowLeft className="w-4 h-4" />
            Back to My Launches
          </button>
          <h1 className="text-heading-1 font-heading-1 text-default-font">
            Launch New Collection
          </h1>
          <p className="text-body font-body text-subtext-color mt-1">
            Create your NFT collection in a few simple steps.{' '}
            <Link to="/launchpad/guide" target="_blank" className="text-brand-600 hover:underline">
              Read the Creator Guide
            </Link>
          </p>
        </div>

        {/* Step Indicator - only shown from step 1 onward */}
        {currentStep >= 1 && (
          <div className="mb-8">
            <HorizontalWizardStepper
              steps={WIZARD_STEPS}
              currentStep={currentStep}
              stepStatus={stepStatus}
              onStepClick={launchId ? goToStep : undefined}
            />
          </div>
        )}

        {/* Step Title - only shown from step 1 onward */}
        {currentStep >= 1 && (
          <div className="mb-6">
            <h2 className="text-heading-3 font-heading-3 text-default-font">
              Step {currentStep}: {WIZARD_STEPS.find((s) => s.number === currentStep)?.title}
            </h2>
            <p className="text-body font-body text-subtext-color">
              {STEP_DESCRIPTIONS[currentStep as Exclude<WizardStepNumber, 0>]}
            </p>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-error-100 border border-error-200 rounded-lg">
            <p className="text-body font-body text-error-700">{error}</p>
          </div>
        )}

        {/* Step 0: Project Association Pre-Step */}
        {currentStep === 0 && preWizardScreen === 'project' && (
          <div>
            <h2 className="text-heading-3 font-heading-3 text-default-font mb-2">
              Does this collection belong to a project?
            </h2>
            <p className="text-body font-body text-subtext-color mb-6">
              A project groups related collections and tokens under one brand &mdash; for example, a game with multiple NFT series and a token. Most single-collection launches don't need one. You can always add your collection to a project later.
            </p>

            <div className="flex flex-col gap-4">
              {/* No project */}
              <button
                type="button"
                onClick={() => { setProjectChoice('none'); setSelectedProjectId(null); }}
                className={`p-6 rounded-xl border-2 text-left transition-colors ${
                  projectChoice === 'none'
                    ? 'border-brand-600 bg-brand-50'
                    : 'border-neutral-border hover:border-neutral-400'
                }`}
              >
                <div className="text-body-bold font-body-bold text-default-font mb-1">No project</div>
                <p className="text-caption font-caption text-subtext-color">
                  Launch as an independent collection. You can add it to a project later.
                </p>
              </button>

              {/* Existing project */}
              <button
                type="button"
                onClick={() => setProjectChoice('existing')}
                className={`p-6 rounded-xl border-2 text-left transition-colors ${
                  projectChoice === 'existing'
                    ? 'border-brand-600 bg-brand-50'
                    : 'border-neutral-border hover:border-neutral-400'
                }`}
              >
                <div className="text-body-bold font-body-bold text-default-font mb-1">Add to existing project</div>
                <p className="text-caption font-caption text-subtext-color mb-3">
                  Associate this collection with a project you already manage.
                </p>
                {projectChoice === 'existing' && (
                  <div onClick={(e) => e.stopPropagation()}>
                    {myProjects.length > 0 ? (
                      <Select
                        value={selectedProjectId || ''}
                        onValueChange={(val) => setSelectedProjectId(val || null)}
                        placeholder="Select a project..."
                        className="w-full mt-1"
                      >
                        {myProjects.map((p) => (
                          <Select.Item key={p.id} value={p.id}>{p.name}</Select.Item>
                        ))}
                      </Select>
                    ) : (
                      <p className="text-caption font-caption text-subtext-color mt-1">
                        {projectsLoaded ? "You don't have any projects yet." : 'Loading projects...'}
                      </p>
                    )}
                  </div>
                )}
              </button>

              {/* Create new project */}
              <button
                type="button"
                onClick={() => setProjectChoice('new')}
                className={`p-6 rounded-xl border-2 text-left transition-colors ${
                  projectChoice === 'new'
                    ? 'border-brand-600 bg-brand-50'
                    : 'border-neutral-border hover:border-neutral-400'
                }`}
              >
                <div className="text-body-bold font-body-bold text-default-font mb-1">Create new project</div>
                <p className="text-caption font-caption text-subtext-color mb-3">
                  Start a new project for this collection.
                </p>
                {projectChoice === 'new' && (
                  <div className="flex flex-col gap-3 mt-2" onClick={(e) => e.stopPropagation()}>
                    <TextField
                      label={<span>Project Name <span className="text-error-600">*</span></span>}
                    >
                      <TextField.Input
                        value={newProjectName}
                        onChange={(e) => setNewProjectName(e.target.value)}
                        placeholder="My Project"
                        maxLength={255}
                      />
                    </TextField>
                    <TextArea
                      label="Description (optional)"
                    >
                      <TextArea.Input
                        value={newProjectDescription}
                        onChange={(e) => setNewProjectDescription(e.target.value)}
                        placeholder="A brief description of your project"
                        maxLength={5000}
                      />
                    </TextArea>
                    <p className="text-caption font-caption text-subtext-color">
                      You can add images, links, and tags after creating the project.
                    </p>
                  </div>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Step 0: Collection Type Selection */}
        {currentStep === 0 && preWizardScreen === 'type' && (
          <div>
            <h2 className="text-heading-3 font-heading-3 text-default-font mb-2">
              Choose Collection Type
            </h2>
            <p className="text-body font-body text-subtext-color mb-6">
              Select the type of collection you want to create.
            </p>
            <div className="flex flex-col gap-4">
              {/* Standard Collection Card */}
              <button
                type="button"
                onClick={() => setSelectedType('standard')}
                className={`p-6 rounded-xl border-2 text-left transition-colors ${
                  selectedType === 'standard'
                    ? 'border-brand-600 bg-brand-50'
                    : 'border-neutral-border hover:border-neutral-400'
                }`}
              >
                <div className="text-heading-3 font-heading-3 text-default-font mb-2">
                  Pre-Generated (Standard)
                </div>
                <p className="text-body font-body text-subtext-color mb-3">
                  The traditional NFT launch. Each NFT is a complete, pre-generated image with a fixed combination of traits.
                </p>
                <ul className="list-disc pl-4 text-caption font-caption text-subtext-color space-y-1">
                  <li>Full NFT images are pre-made before launch</li>
                  <li>Each NFT has a set trait combination that never changes</li>
                  <li>Collectors mint a random NFT from the collection</li>
                  <li>What you mint is what you get — no modifications</li>
                  <li>Best choice for most collections</li>
                </ul>
              </button>

              {/* Edition Collection Card */}
              <button
                type="button"
                onClick={() => setSelectedType('edition')}
                className={`p-6 rounded-xl border-2 text-left transition-colors ${
                  selectedType === 'edition'
                    ? 'border-brand-600 bg-brand-50'
                    : 'border-neutral-border hover:border-neutral-400'
                }`}
              >
                <div className="text-heading-3 font-heading-3 text-default-font mb-2 flex items-center gap-2">
                  Editions (Art Prints)
                  <span className="text-caption font-caption text-brand-600 bg-brand-100 px-2 py-0.5 rounded">New!</span>
                </div>
                <p className="text-body font-body text-subtext-color mb-3">
                  Sell your artworks like limited-edition prints. You choose how many copies
                  of each piece exist, and collectors pick exactly the artwork they want.
                </p>
                <ul className="list-disc pl-4 text-caption font-caption text-subtext-color space-y-1">
                  <li>Upload each artwork and set its number of copies (e.g. 50)</li>
                  <li>Buyers browse the gallery and mint the piece they like</li>
                  <li>Each copy is numbered (e.g. #12/50)</li>
                  <li>Optional price per artwork, or one launch-wide price</li>
                  <li>Once an edition sells out, no more copies can ever be minted</li>
                </ul>
              </button>

              {/* Composable Collection Card */}
              {featureFlags.composable ? (
                <button
                  type="button"
                  onClick={() => setSelectedType('composable')}
                  className={`p-6 rounded-xl border-2 text-left transition-colors ${
                    selectedType === 'composable'
                      ? 'border-brand-600 bg-brand-50'
                      : 'border-neutral-border hover:border-neutral-400'
                  }`}
                >
                  <div className="text-heading-3 font-heading-3 text-default-font mb-2 flex items-center gap-2">
                    Build Your Own
                    <span className="text-caption font-caption text-brand-600 bg-brand-100 px-2 py-0.5 rounded">New!</span>
                  </div>
                  <p className="text-body font-body text-subtext-color mb-3">
                    Collectors build their own NFTs. Minters receive single-trait NFTs, then combine them to create completely unique custom NFTs.
                  </p>
                  <ul className="list-disc pl-4 text-caption font-caption text-subtext-color space-y-1">
                    <li>Each mint gives a single-trait NFT (e.g. a hat, a background)</li>
                    <li>Holders choose which traits to combine and build a new NFT</li>
                    <li>Building burns the input NFTs and mints a fresh custom NFT</li>
                    <li>Collectors craft their own unique NFTs from the traits they collect</li>
                    <li>Exclusive to XRP Deals</li>
                  </ul>
                </button>
              ) : (
                <div className="p-6 rounded-xl border-2 border-neutral-border opacity-50 cursor-not-allowed relative">
                  <span className="absolute top-3 right-3 text-caption font-caption text-subtext-color bg-neutral-100 px-2 py-0.5 rounded">
                    Coming Soon
                  </span>
                  <div className="text-heading-3 font-heading-3 text-default-font mb-2">
                    Coming Soon!
                  </div>
                  <p className="text-body font-body text-subtext-color">
                    We're cooking up a brand new way to launch, mint, and collect NFTs — something nobody on XRPL has done before. It's going to change how you think about NFT collections entirely.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step Content */}
        {currentStep === 1 && (
          <WizardStepCollectionInfo
            value={collectionInfo}
            onChange={(updates) => setCollectionInfo((prev) => ({ ...prev, ...updates }))}
            errors={{ taxon: taxonError || undefined }}
            isSlugChecking={slugChecking}
            isSlugAvailable={slugAvailable}
            onSlugManualEdit={() => setSlugManuallyEdited(true)}
          />
        )}

        {currentStep === 2 && launchId && launch && (
          <WizardStepImages
            launchId={launchId}
            thumbnailUrl={launch.thumbnail_url}
            bannerUrl={launch.banner_url}
            bannerPositionY={launch.banner_position_y ?? 50}
            onUploadComplete={(updates) => {
              setLaunch((prev) =>
                prev
                  ? {
                      ...prev,
                      thumbnail_url: updates.thumbnailUrl ?? prev.thumbnail_url,
                      banner_url: updates.bannerUrl !== undefined ? (updates.bannerUrl || null) : prev.banner_url,
                      banner_position_y: updates.bannerPositionY ?? prev.banner_position_y,
                    }
                  : null
              );
            }}
            onError={setError}
          />
        )}

        {currentStep === 3 && launchId && launch && (
          <WizardStepPricing
            value={pricingData}
            onChange={setPricingData}
          />
        )}

        {currentStep === 4 && launchId && launch && !isComposable && !isEdition && (
          <WizardStepAssets
            launchId={launchId}
            totalItems={launch.total_items}
            onUploadComplete={async (result) => {
              await refreshLaunch();
            }}
            onError={setError}
          />
        )}

        {currentStep === 4 && launchId && launch && isEdition && (
          <WizardStepEditions
            launchId={launchId}
            defaultPriceDrops={pricingData.mintPriceDrops}
            onUploadComplete={async () => {
              await refreshLaunch();
            }}
            onUploadingChange={setTraitUploading}
            onError={setError}
          />
        )}

        {currentStep === 4 && launchId && launch && isComposable && (
          <WizardStepComposableTraits
            launchId={launchId}
            baseImageUrl={launch.base_image_url}
            onBaseImageUploaded={(url) => {
              setLaunch((prev) => prev ? { ...prev, base_image_url: url } : null);
            }}
            onUploadingChange={setTraitUploading}
            onError={setError}
          />
        )}

        {currentStep === 5 && launchId && (
          <WizardStepReview
            launchId={launchId}
            onError={setError}
          />
        )}

        {/* Navigation */}
        <div className="flex items-center justify-between mt-8 pt-6 border-t border-neutral-border">
          <Button
            variant="neutral-tertiary"
            onClick={handleBack}
            disabled={loading || traitUploading}
          >
            Back
          </Button>

          <div className="flex items-center gap-3">
            {/* Save & Exit - available for steps 2-4 */}
            {currentStep > 1 && currentStep < 5 && (
              <Button
                variant="neutral-secondary"
                onClick={handleSaveAndExit}
                disabled={loading || traitUploading}
              >
                Save & Exit
              </Button>
            )}

            {/* Skip - available for steps 2-4 */}
            {canSkip && (
              <Button variant="neutral-secondary" onClick={handleSkip} disabled={loading || traitUploading}>
                Skip
              </Button>
            )}

            {/* Primary action */}
            {currentStep === 0 && preWizardScreen === 'project' ? (
              <Button
                onClick={handleProjectContinue}
                disabled={
                  creatingProject ||
                  (projectChoice === 'existing' && !selectedProjectId) ||
                  (projectChoice === 'new' && !newProjectName.trim())
                }
                loading={creatingProject}
                iconRight={<FeatherArrowRight />}
              >
                Continue
              </Button>
            ) : currentStep === 0 && preWizardScreen === 'type' ? (
              <Button
                onClick={handleStep0Continue}
                iconRight={<FeatherArrowRight />}
              >
                Continue
              </Button>
            ) : currentStep === 1 ? (
              <Button
                onClick={handleStep1Complete}
                disabled={!isStep1Valid() || loading}
                loading={loading}
                iconRight={<FeatherArrowRight />}
              >
                {launchId ? 'Continue' : 'Create Draft & Continue'}
              </Button>
            ) : currentStep === 5 ? (
              <Button onClick={handleGoToDashboard} iconRight={<FeatherArrowRight />}>
                Go to Dashboard
              </Button>
            ) : (
              <Button
                onClick={handleContinue}
                disabled={loading || traitUploading}
                loading={loading}
                iconRight={<FeatherArrowRight />}
              >
                Continue
              </Button>
            )}
          </div>
        </div>
      </div>
    </DefaultPageLayout>
  );
};
